import { useCallback, useEffect, useState } from 'react';

import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	requestPublishedReviewInEditMode,
	selectPublishedSuggestion,
	startPublishedSuggestions,
} from './publishedSuggestionsStore.ts';
import { usePublishedSuggestionsState } from './usePublishedSuggestionsState.ts';

const useOpenSuggestionInEditMode = (contentId: string | undefined) => {
	const { loadEditor } = useEditPageLoadingActions();
	const [contentType] = useContentType();
	const [spaceKey = ''] = usePageSpaceKey();
	const isEditorPage = useIsEditorPage();

	return useCallback(
		(suggestionId?: string) => {
			if (!contentId || isEditorPage) {
				return;
			}
			if (suggestionId) {
				selectPublishedSuggestion(suggestionId);
			}
			loadEditor({
				contentId,
				spaceKey,
				contentType,
				redirectUrl: getUrlForContentType({
					contentType,
					spaceKey,
					contentId,
					redirectToEditor: true,
				}),
			});
		},
		[contentId, contentType, isEditorPage, loadEditor, spaceKey],
	);
};

export type PublishedSuggestionsPanelActions = {
	isHeadingToEditMode: boolean;
	openInEditMode: (suggestionId?: string) => void;
	startReviewInEditMode: () => void;
};

export const usePublishedSuggestionsPanel = (
	isPublishedMode: boolean,
): PublishedSuggestionsPanelActions => {
	const { contentId } = usePublishedSuggestionsState();
	const [pageContentId] = usePageContentId();
	const isEditorPage = useIsEditorPage();
	const { cloudId } = useSessionData();

	useEffect(() => {
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The suggested edits experiment is the kill switch for the view mode Suggestions panel subgate.
			isPublishedMode &&
			!isEditorPage &&
			pageContentId &&
			pageContentId !== contentId &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
			fg('confluence_suggestions_panel_view_mode')
		) {
			startPublishedSuggestions(pageContentId, cloudId);
		}
	}, [cloudId, contentId, isEditorPage, isPublishedMode, pageContentId]);

	const openSuggestionInEditMode = useOpenSuggestionInEditMode(contentId);
	const [isOpeningEditMode, setIsOpeningEditMode] = useState(false);
	const openInEditMode = useCallback(
		(suggestionId?: string) => {
			setIsOpeningEditMode(true);
			openSuggestionInEditMode(suggestionId);
		},
		[openSuggestionInEditMode],
	);
	const startReviewInEditMode = useCallback(() => {
		requestPublishedReviewInEditMode();
		openInEditMode();
	}, [openInEditMode]);

	return {
		isHeadingToEditMode: isOpeningEditMode || isEditorPage,
		openInEditMode,
		startReviewInEditMode,
	};
};
